/* @flow */
/*************************************************************************
* ADOBE CONFIDENTIAL
* ___________________
*
*  Copyright 2018 Adobe
*  All Rights Reserved.
*
* NOTICE: All information contained herein is, and remains
* the property of Adobe and its suppliers, if any. The intellectual
* and technical concepts contained herein are proprietary to Adobe
* and its suppliers and are protected by all applicable intellectual
* property laws, including trade secret and copyright laws.
* Dissemination of this information or reproduction of this material
* is strictly forbidden unless prior written permission is obtained
* from Adobe.
**************************************************************************/

/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import './Footer.scss';
import CreativeCloud from '@react/react-spectrum/Icon/CreativeCloud';
import {getLocalizedURL} from '@intl/adobe-locales';
import Twitter from '@react/react-spectrum/Icon/Twitter';
import Instagram from '@react/react-spectrum/Icon/Instagram';
import openUrl from '../../utils/openUrl';
import {genericAnalyticEvent} from '../../utils/AnalyticsUtil';
import getConfig from '../../config';

const isBrowser = typeof window !== `undefined` && typeof document !== `undefined`;

const onClickDownload = (e, locale, props) => {
	const {appendUrlWithAnalytics, footerContext} = props;
	const url = appendUrlWithAnalytics(`/apps/download/creative-cloud?locale=${locale}`);
	handleAnalyticEvents('click', {
		subtype: e.target.dataset.subtype,
		url
	}, props).then(result =>
		footerContext.downloadCC(url)
	);
};

const onClickWebApp = (e, props) => {
	const {appendUrlWithAnalytics} = props;
	const url = appendUrlWithAnalytics('/cc');
	handleAnalyticEvents('click', {
		subtype: e.target.dataset.subtype,
		url
	}, props).then(result =>
		openUrl(url, '_self')
	);
};

const handleAnalyticEvents = (type, params, props) => {
	let eventObj;
	switch (type) {
		case 'render':
			eventObj = {
				event: {
					subcategory: 'Landing',
					type: 'render',
					subtype: 'Footer',
					value: 'Trail Head'
				}
			};
			break;
		case 'click':
			eventObj = {
				event: {
					subcategory: 'Landing',
					type: 'click',
					subtype: params.subtype,
					value: 'Trail Head'
				},
				content: {
					action: params.url
				}
			};
			break;
	}
	const data = genericAnalyticEvent(eventObj);
	const {footerContext} = props;
	return footerContext.analyticsCallbackHandler(data);
};

const onClickCookiePref = () => {
	if (adobePrivacy && adobePrivacy.showConsentPopup) {
		adobePrivacy.showConsentPopup();
	}
};

const postClickEvents = (e, url, locale, props) => {
	const { subtype } = e.currentTarget.dataset;
	handleAnalyticEvents('click', {subtype, url}, props);
	if (locale) {
		openUrl(getLocalizedURL(url, locale));
	} else {
		openUrl(url);
	}
};

const navigateToWhatsNew = (e, locale, props) => {
	const { subtype } = e.currentTarget.dataset;
	let newPath = '/discover/article/whats-new';

	if (isBrowser && window.location.pathname.match(`${locale}`)) {
		newPath = `/${locale}${newPath}`;
	}

	handleAnalyticEvents('click', {subtype, url: newPath}, props);
	openUrl(newPath, '_self');
};

const navigateToLibraries = (e, locale, props) => {
	const { subtype } = e.currentTarget.dataset;
	let newPath = '/libraries';

	if (isBrowser && window.location.pathname.match(`${locale}`)) {
		newPath = `/${locale}${newPath}`;
	}

	handleAnalyticEvents('click', {subtype, url: newPath}, props);

	openUrl(newPath, '_self');
};

/**
 * Detect key press
 * @param {any} event 
 */
const onKeyDown = (event: any) => {
	if (event.key === ' ' || event.key === 'Enter' || event.which === 13 || event.which === 32) {
		event.currentTarget.click();
	}
}


const Footer = (props) => {
	const {hideLanguageSwitcher, source, intl, environment, handleLanguageChange, appendUrlWithAnalytics, footerContext} = props;
	footerContext.setLocale(intl.locale);

	useEffect(() => {
		handleAnalyticEvents('render', {}, props);
	}, []);

	const baseURL = getConfig('ADOBE_BASE_GOURL', environment);
	const homeURL = getConfig('HOME_URL', environment);
	const helpxURL = getConfig('HELPX_URL', environment);
	const adobeURL = getConfig('ADOBE_URL', environment);
	const communityURL = getConfig('COMMUNITY_URL', environment);

	return (
		<footer aria-label={intl.formatMessage({ id: 'app.footer.aria.label' })} role='contentinfo' className='trailhead-footer-container'>
			<div className='footer-inner'>
				<div className='footer-top'>
					<div className='footer-left'>
						<div>
							<h3><CreativeCloud size='M' />{intl.formatMessage({ id: 'app.footer.creativeCloud' })}</h3>
							{!hideLanguageSwitcher && <div className='select-language-text'>{intl.formatMessage({ id: 'app.footer.selectLanguage' })}</div>}
							<div className='lang-switcher-container'>{(isBrowser && !hideLanguageSwitcher) && <LanguageSwitcher intl={intl} supportedLangs={getConfig('SUPPORTED_LANGUAGES', environment)} handleLanguageChange={handleLanguageChange} />}</div>
							<div className='social-media-icons'>
								<div
									data-subtype='twitter' 
									onClick={(e) => postClickEvents(e, getConfig('TWITTER_URI', environment)[source], undefined, props)}
									onKeyDown={(e) => onKeyDown(e)}
									tabIndex={0}>
									<Twitter />
								</div>
								<div 
									data-subtype='instagram' 
									onClick={(e) => postClickEvents(e, getConfig('INSTAGRAM_URI', environment)[source], undefined, props)}
									onKeyDown={(e) => onKeyDown(e)}
									tabIndex={0}>
									<Instagram />
								</div>
							</div>
						</div>
					</div>
					<div className='footer-right'>
						<div>
							<h3>{intl.formatMessage({ id: 'app.footer.learnAndSupport' })}</h3>
							<a 	data-subtype='help-center' 
								onClick={(e) => postClickEvents(e, `${helpxURL}/support.html`, intl.locale, props)}
								onKeyDown={(e) => onKeyDown(e)}
								role='link' 
								tabIndex={0} 
								rel='noopener noreferrer'>
								{intl.formatMessage({ id: 'app.footer.helpCenter' })}
							</a>
							<a 	data-subtype='support-community' 
								onClick={(e) => postClickEvents(e, communityURL, intl.locale, props)}
								onKeyDown={(e) => onKeyDown(e)}
								role='link' 
								tabIndex={0} 
								rel='noopener noreferrer'>
								{intl.formatMessage({ id: 'app.footer.communityForums' })}
							</a>
							<a 	data-subtype='enterprise-support' 
								onClick={(e) => postClickEvents(e, `${helpxURL}/contact/enterprise-support.html`, intl.locale, props)}
								onKeyDown={(e) => onKeyDown(e)}
								role='link' 
								tabIndex={0} 
								rel='noopener noreferrer'>
								{intl.formatMessage({ id: 'app.footer.enterpriseSupport' })}
							</a>
							<a 	data-subtype='download-and-install' 
								onClick={(e) => postClickEvents(e, `${helpxURL}/download-install.html`, intl.locale, props)}
								onKeyDown={(e) => onKeyDown(e)}
								role='link' 
								tabIndex={0} 
								rel='noopener noreferrer'>
								{intl.formatMessage({ id: 'app.footer.downloadAndInstall' })}
							</a>
						</div>
						<div>
							<h3>{intl.formatMessage({ id: 'app.footer.getCreativeCloud' })}</h3>
							<a 	data-subtype='desktop-app' 
								role='link' 
								tabIndex={0} 
								rel='noopener noreferrer' 
								onClick={(e) => onClickDownload(e, intl.locale, props)}
								onKeyDown={(e) => onKeyDown(e)}>
								{intl.formatMessage({ id: 'app.footer.desktopApp' })}
							</a>
							<a 	data-subtype='ios-app' 
								onClick={(e) => postClickEvents(e, intl.formatMessage({ id: 'app.footer.iOSApp.url' }), intl.locale, props)}
								onKeyDown={(e) => onKeyDown(e)}
								role='link' 
								tabIndex={0} 
								rel='noopener noreferrer'>
								{intl.formatMessage({ id: 'app.footer.iOSApp' })}
							</a>
							<a 	data-subtype='android-app' 
								onClick={(e) => postClickEvents(e, intl.formatMessage({ id: 'app.footer.androidApp.url' }), intl.locale, props)}
								onKeyDown={(e) => onKeyDown(e)}
								role='link' 
								tabIndex={0} 
								rel='noopener noreferrer'>{intl.formatMessage({ id: 'app.footer.androidApp' })}
							</a>
							<a 	data-subtype='web-app' 
								onClick={(e) => postClickEvents(e, '', undefined, props)}
								onKeyDown={(e) => onKeyDown(e)}
								target='_self' 
								tabIndex={0} 
								rel='noopener noreferrer' 
								onClick={(e) => {onClickWebApp(e, props);}}>
								{intl.formatMessage({ id: 'app.footer.webApp' })}
							</a>
						</div>
						<div>
							<h3>{intl.formatMessage({ id: 'app.footer.more' })}</h3>
							<a data-subtype='whats-new' 
								onClick={(e) => navigateToWhatsNew(e, intl.locale, props)}
								onKeyDown={(e) => onKeyDown(e)}
								role='link' 
								tabIndex={0} 
								rel='noopener noreferrer'>
								{intl.formatMessage({ id: 'app.footer.whatsNew' })}
							</a>
							<a 	data-subtype='faq' 
								onClick={(e) => postClickEvents(e, `${helpxURL}/creative-cloud/faq.html`, intl.locale, props)}
								onKeyDown={(e) => onKeyDown(e)}
								role='link' 
								tabIndex={0} 
								rel='noopener noreferrer'>
								{intl.formatMessage({ id: 'app.footer.faqs' })}
							</a>
							<a 	data-subtype='plans-and-pricing'
								onClick={(e) => postClickEvents(e, `${adobeURL}/creativecloud/plans.html?promoid=BWBX3293&mv=other`, intl.locale, props)}
								onKeyDown={(e) => onKeyDown(e)}
								role='link' 
								tabIndex={0} 
								rel='noopener noreferrer'>
								{intl.formatMessage({ id: 'app.footer.plansAndPricing' })}
							</a>
							{<a data-subtype='libraries' 
								href={isBrowser && window.location.pathname.match(intl.locale) ? `/${intl.locale}/libraries` : `/libraries`}
								onKeyDown={(e) => onKeyDown(e)}
								role='link' 
								tabIndex={0} 
								rel='noopener noreferrer'>
								{intl.formatMessage({ id: 'app.footer.libraries' })}
							</a>}
						</div>
					</div>
				</div>
				<div className='footer-bottom'>
					<div className='footer-copyright'>
						&copy;{intl.formatMessage({ id: 'app.footer.copyright' })}
					</div>
					<div>
						<a	
							data-subtype='terms-of-use'
							onClick={(e) => postClickEvents(e, `${baseURL}${intl.formatMessage({ id: 'app.footer.tou.url' })}`, undefined, props)}
							onKeyDown={(e) => onKeyDown(e)}
							role='link'
							tabIndex={0}
							rel='noopener noreferrer'
							aria-label={intl.formatMessage({ id: 'app.footer.tou' })}
						>
							<span>{intl.formatMessage({ id: 'app.footer.tou' })}</span>
						</a>
					</div>
					<div>
						<a
							data-subtype='privacy-policy'
							onClick={(e) => postClickEvents(e, `${baseURL}${intl.formatMessage({ id: 'app.footer.privacyPolicy.url' })}`, undefined, props)}
							onKeyDown={(e) => onKeyDown(e)}
							role='link'
							tabIndex={0}
							rel='noopener noreferrer'
							aria-label={intl.formatMessage({ id: 'app.footer.privacyPolicy' })}
						>
							<span>{intl.formatMessage({ id: 'app.footer.privacyPolicy' })}</span>
						</a>
					</div>
					<div>
						<a	
							data-subtype='cookies'
							onClick={(e) => postClickEvents(e, `${baseURL}${intl.formatMessage({ id: 'app.footer.cookies.url' })}`, undefined, props)}
							onKeyDown={(e) => onKeyDown(e)}
							role='link'
							tabIndex={0}
							rel='noopener noreferrer'
							aria-label={intl.formatMessage({ id: 'app.footer.cookies' })}
						>
							<span>{intl.formatMessage({ id: 'app.footer.cookies' })}</span>
						</a>
					</div>
					<div>
						<div className='evidon-notice-link consent-link'>
							<span onClick={() => onClickCookiePref()} onKeyDown={(e) => onKeyDown(e)} tabIndex={0}>{intl.formatMessage({ id: 'app.footer.cookiePreferences' })}</span>
							<div className='consent-link-hidden'></div>
						</div>
					</div>
					<div>
						<a
							data-subtype='do-not-sell'
							onClick={(e) => postClickEvents(e, `${adobeURL}${intl.formatMessage({ id: 'app.footer.doNotSell.url' })}`, undefined, props)}
							onKeyDown={(e) => onKeyDown(e)}
							role='link'
							tabIndex={0}
							rel='noopener noreferrer'
							aria-label={intl.formatMessage({ id: 'app.footer.doNotSell' })}
						>
							<span>{intl.formatMessage({ id: 'app.footer.doNotSell' })}</span>
						</a>
					</div>
				</div>
			</div>

		</footer>
	);
}

export default Footer;